import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { weekFromIsoDate } from '../../lib/weekFromIsoDate';

/**
 * KeyboardNav — scorciatoie di navigazione temporale globali.
 *
 * Comportamento:
 *   [  → periodo precedente (giorno / settimana / mese, a seconda della rotta)
 *   ]  → periodo successivo
 *   T  → salta alla vista Oggi
 *
 * L'unità di step è dedotta dalla URL:
 *   /oggi                          → giorno (anchor: today)
 *   /periodo/giorno/YYYY-MM-DD     → giorno
 *   /periodo/settimana/YYYY-Www    → settimana
 *   /periodo/mese/YYYY-MM          → mese
 *
 * In altre viste (dashboard, obiettivo, ecc.) solo `T` è attivo.
 * Il listener ignora input, textarea, contenteditable e i modifier
 * meta/ctrl/alt per non collidere con shortcut del browser.
 */
export default function KeyboardNav() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toUpperCase();
      const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)
        || e.target?.isContentEditable;
      if (isInput) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const path = location.pathname;
      const key = e.key;

      // "T" o "t" → sempre Oggi
      if (key === 't' || key === 'T') {
        e.preventDefault();
        navigate('/oggi');
        return;
      }

      if (key !== '[' && key !== ']') return;
      const dir = key === ']' ? +1 : -1;

      // Giorno: /oggi (anchor: oggi) o /periodo/giorno/YYYY-MM-DD
      let m;
      if (path === '/oggi') {
        const now = new Date();
        const iso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        e.preventDefault();
        navigate(`/periodo/giorno/${addDays(iso, dir)}`);
      } else if ((m = path.match(/\/periodo\/giorno\/(\d{4}-\d{2}-\d{2})/))) {
        e.preventDefault();
        navigate(`/periodo/giorno/${addDays(m[1], dir)}`);
      } else if ((m = path.match(/\/periodo\/settimana\/(\d{4}-W\d{2})/))) {
        e.preventDefault();
        navigate(`/periodo/settimana/${addWeeks(m[1], dir)}`);
      } else if ((m = path.match(/\/periodo\/mese\/(\d{4}-\d{2})/))) {
        e.preventDefault();
        navigate(`/periodo/mese/${addMonths(m[1], dir)}`);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [location.pathname, navigate]);

  return null;
}

function addDays(iso, delta) {
  const [y, mo, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
}

function addWeeks(isoWeek, delta) {
  // Prendo il lunedì della settimana, sommo delta*7 giorni, ricavo la nuova ISO week.
  const [y, w] = isoWeek.split('-W').map(Number);
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - (jan4Day - 1) + 7 * (w - 1) + 7 * delta);
  return weekFromIsoDate(monday.toISOString().slice(0, 10));
}

function addMonths(isoMonth, delta) {
  const [y, mo] = isoMonth.split('-').map(Number);
  const dt = new Date(Date.UTC(y, mo - 1 + delta, 1));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
}
